/**
 * Profile Settings Page
 * User profile management with image crop
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Navbar, Footer } from '../../components';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { isValidPhone, isValidName, sanitizePhone } from '../../utils/validationUtils';
import { formatDate } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

function getCroppedImage(image, crop, outputSize = 200) {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    outputSize,
    outputSize
  );

  return canvas.toDataURL('image/png');
}

function centerAspectCrop(mediaWidth, mediaHeight) {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 80 }, 1, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight
  );
}

const Profile = () => {
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({ name: '', bio: '', phone: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [avatarPreview, setAvatarPreview] = useState('');

  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const fileInputRef = useRef(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const data = await api.getUserProfile();
      const userData = data.user;
      setUser(userData);
      setFormData({
        name: userData.name || '',
        bio: userData.bio || '',
        phone: userData.phone || '',
      });
      setAvatarPreview(userData.avatar || '');
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    imgRef.current = e.currentTarget;
    setCrop(centerAspectCrop(width, height));
  }, []);

  const applyCrop = () => {
    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) return;
    const croppedDataUrl = getCroppedImage(imgRef.current, completedCrop);
    setAvatarPreview(croppedDataUrl);
    setShowCropModal(false);
    setSelectedImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    const errors = {};
    if (!isValidName(formData.name)) errors.name = 'Name must be at least 2 characters';
    if (formData.phone && !isValidPhone(formData.phone)) errors.phone = 'Please enter a valid phone number (7-15 digits)';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSaving(false);
      return;
    }

    try {
      const data = await api.updateUserProfile({ ...formData, avatar: avatarPreview });
      setUser(data.user);
      setSuccess('Profile updated successfully');
      window.location.reload();
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-50">
        <Navbar />
        <div className="pt-20 pb-12 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="lh-profile-page min-h-screen bg-dark-50">
      <SEO title="Profile Settings" noIndex />
      <Navbar />

      <main className="lh-profile__main pt-20 pb-12">
        <div className="lh-profile__container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lh-profile__header mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-dark-900 mb-1">Profile</h1>
            <p className="text-dark-500">Manage your account information and preferences</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Profile Picture */}
            <div className="lg:col-span-1">
              <div className="card p-6">
                <h2 className="text-lg font-bold text-dark-900 mb-4">Profile Picture</h2>
                <div className="flex flex-col items-center">
                  <div className="relative mb-4">
                    <div className="w-32 h-32 bg-primary-500 rounded-full flex items-center justify-center text-white text-4xl font-semibold overflow-hidden">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span>{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowPhotoMenu(!showPhotoMenu)}
                      className="btn-outline"
                    >
                      Edit Photo
                    </button>
                    {showPhotoMenu && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowPhotoMenu(false)} />
                        <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-44 bg-white rounded-lg shadow-lg border border-dark-100 z-20 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => {
                              setShowPhotoMenu(false);
                              fileInputRef.current?.click();
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm text-dark-700 hover:bg-dark-50 transition-colors flex items-center gap-2"
                          >
                            <svg className="w-4 h-4 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Change Photo
                          </button>
                          {avatarPreview && (
                            <button
                              type="button"
                              onClick={() => {
                                setShowPhotoMenu(false);
                                setAvatarPreview('');
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 border-t border-dark-100"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete Photo
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <p className="text-xs text-dark-500 mt-2 text-center">
                    Recommended: 200x200px, Max 5MB
                  </p>
                </div>
              </div>

              {/* Read-only Info */}
              <div className="card p-6 mt-6">
                <h2 className="text-lg font-bold text-dark-900 mb-4">Account Information</h2>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-dark-500">Email</label>
                    <p className="text-dark-900 font-medium">{user?.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-dark-500">Role</label>
                    <p className="text-dark-900 font-medium capitalize">{user?.role}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-dark-500">Status</label>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      user?.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                    }`}>
                      {user?.status}
                    </span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-dark-500">Member Since</label>
                    <p className="text-dark-900 font-medium">
                      {user?.createdAt ? formatDate(user.createdAt) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="card p-6">
                <h2 className="text-lg font-bold text-dark-900 mb-6">Personal Information</h2>

                {error && <div className="mb-4 p-3 bg-red-100 text-red-600 rounded-lg text-sm">{error}</div>}
                {success && <div className="mb-4 p-3 bg-green-100 text-green-600 rounded-lg text-sm">{success}</div>}

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setFieldErrors(prev => ({ ...prev, name: null })); }}
                      required
                      minLength={2}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${fieldErrors.name ? 'border-red-400' : 'border-dark-200'}`}
                    />
                    {fieldErrors.name && <p className="text-xs text-red-500 mt-1">{fieldErrors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1.5">Bio</label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about yourself..."
                      className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="text-xs text-dark-500 mt-1">{formData.bio.length}/500 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => { setFormData({ ...formData, phone: sanitizePhone(e.target.value) }); setFieldErrors(prev => ({ ...prev, phone: null })); }}
                      placeholder="+91 98765 43210"
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${fieldErrors.phone ? 'border-red-400' : 'border-dark-200'}`}
                    />
                    {fieldErrors.phone ? (
                      <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>
                    ) : (
                      <p className="text-xs text-dark-400 mt-1">Include country code, e.g. +91 98765 43210</p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button type="submit" disabled={saving} className="btn-primary flex-1">
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ name: user?.name || '', bio: user?.bio || '', phone: user?.phone || '' });
                        setAvatarPreview(user?.avatar || '');
                        setError('');
                        setSuccess('');
                      }}
                      className="btn-outline"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* Crop Modal */}
      {showCropModal && selectedImage && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-dark-900 mb-1">Crop Profile Picture</h3>
            <p className="text-sm text-dark-500 mb-4">
              Drag to reposition. Resize by pulling the corners.
            </p>
            <div className="flex justify-center bg-dark-100 rounded-lg overflow-hidden">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
                minWidth={50}
                minHeight={50}
              >
                <img
                  src={selectedImage}
                  alt="Crop preview"
                  onLoad={onImageLoad}
                  style={{ maxHeight: '60vh', maxWidth: '100%' }}
                />
              </ReactCrop>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button
                onClick={() => { setShowCropModal(false); setSelectedImage(null); }}
                className="btn-outline"
              >
                Cancel
              </button>
              <button onClick={applyCrop} className="btn-primary">
                Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Profile;
